import type { ReactElement } from 'react';

export type IconName =
  | 'search'
  | 'clock'
  | 'check'
  | 'alert'
  | 'chevron'
  | 'arrow'
  | 'close'
  | 'person'
  | 'company'
  | 'space';

/**
 * A small, crisp icon set — real SVGs on the currentColor stroke, sized in
 * pixels — so the marks in search, the summary and empty states read cleanly
 * rather than as thin, mismatched Unicode glyphs.
 */
export function Icon({
  name,
  size = 16,
  className,
  rotate,
}: {
  name: IconName;
  size?: number;
  className?: string;
  /** Degrees, for pointing a chevron down (90) or up (270). */
  rotate?: number;
}): ReactElement {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className,
    style: rotate ? { transform: `rotate(${rotate}deg)` } : undefined,
  };
  switch (name) {
    case 'search':
      return (
        <svg {...props}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20.5 20.5-3.6-3.6" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5V12l3 1.8" />
        </svg>
      );
    case 'check':
      return (
        <svg {...props}>
          <path d="M20 6.5 9.5 17 4 11.5" />
        </svg>
      );
    case 'alert':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5v5.5" />
          <path d="M12 16.5h.01" />
        </svg>
      );
    case 'chevron':
      return (
        <svg {...props}>
          <path d="m9 5 7 7-7 7" />
        </svg>
      );
    case 'arrow':
      return (
        <svg {...props}>
          <path d="M4 12h15" />
          <path d="m13 5 7 7-7 7" />
        </svg>
      );
    case 'close':
      return (
        <svg {...props}>
          <path d="M6 6 18 18" />
          <path d="M18 6 6 18" />
        </svg>
      );
    case 'person':
      return (
        <svg {...props}>
          <circle cx="12" cy="8" r="3.3" />
          <path d="M5.5 19.5a6.5 6.5 0 0 1 13 0" />
        </svg>
      );
    case 'company':
      return (
        <svg {...props}>
          <rect x="6" y="3.5" width="12" height="17" rx="1.2" />
          <path d="M9.5 7.5h.01M14.5 7.5h.01M9.5 11.5h.01M14.5 11.5h.01" />
          <path d="M10.5 20.5v-3h3v3" />
        </svg>
      );
    case 'space':
      return (
        <svg {...props}>
          <path d="M12 3.5 20 8l-8 4.5L4 8l8-4.5Z" />
          <path d="m4 12.5 8 4.5 8-4.5" />
        </svg>
      );
  }
}
